import type {
  CatalogDraft,
  CatalogDraftAction,
  CatalogDraftBulkActionRq,
  CatalogDraftStatus,
  CatalogSkippedDraft,
} from "../../interfaces/CatalogImportObject";
import { activeDraftFlags, isCatalogFlagWarning } from "../../interfaces/CatalogImportObject";

/**
 * Pure draft rules shared by the slice and the review UI (N2): which drafts a
 * bulk action touches, and what status each ends up in. Mirrors the state
 * machine in catalog-import-design.md §5:
 *
 *   PROPOSED / NEEDS_ATTENTION --approve--> APPROVED --unapprove--> PROPOSED / NEEDS_ATTENTION
 *   PROPOSED / NEEDS_ATTENTION / APPROVED --reject--> REJECTED --restore--> PROPOSED / NEEDS_ATTENTION
 */

/** A draft needs a look when it has a WARN flag the curator has not dismissed. */
export function draftNeedsAttention(draft: Pick<CatalogDraft, "flags" | "dismissedFlags">): boolean {
  return activeDraftFlags(draft).some(isCatalogFlagWarning);
}

/** The status a draft would have if it were freshly proposed / restored. */
export const proposedStatusFor = (draft: Pick<CatalogDraft, "flags" | "dismissedFlags">): CatalogDraftStatus =>
  draftNeedsAttention(draft) ? "NEEDS_ATTENTION" : "PROPOSED";

/** The status `action` leads to, or null when the draft's current status does not allow it. */
export function targetStatusFor(
  action: CatalogDraftAction,
  draft: Pick<CatalogDraft, "status" | "flags" | "dismissedFlags">
): CatalogDraftStatus | null {
  const { status } = draft;
  switch (action) {
    case "APPROVE":
      return status === "PROPOSED" || status === "NEEDS_ATTENTION" ? "APPROVED" : null;
    case "REJECT":
      return status === "PROPOSED" || status === "NEEDS_ATTENTION" || status === "APPROVED" ? "REJECTED" : null;
    case "RESTORE":
      return status === "REJECTED" ? proposedStatusFor(draft) : null;
    case "UNAPPROVE":
      return status === "APPROVED" ? proposedStatusFor(draft) : null;
    default:
      return null;
  }
}

/** Which of the cached drafts a bulk request addresses (the server decides for real; this is the local mirror). */
export function draftsTargetedBy(
  rq: CatalogDraftBulkActionRq,
  drafts: readonly CatalogDraft[]
): CatalogDraft[] {
  switch (rq.selector) {
    case "IDS": {
      const ids = new Set(rq.draftIds ?? []);
      return drafts.filter((d) => ids.has(d.draftId));
    }
    case "PAGE":
      return drafts.filter((d) => rq.pageNo !== undefined && d.pageNos.includes(rq.pageNo));
    case "ALL_UNFLAGGED":
      return drafts.filter((d) => d.status === "PROPOSED" && !draftNeedsAttention(d));
    case "ALL_PROPOSED":
      return drafts.filter((d) => d.status === "PROPOSED" || d.status === "NEEDS_ATTENTION");
    default:
      return [];
  }
}

export interface DraftStatusChange {
  draftId: string;
  from: CatalogDraftStatus;
  to: CatalogDraftStatus;
}

/**
 * Local status changes after a successful bulk call: every targeted draft the
 * server did not report as skipped, whose status allows the action.
 */
export function statusChangesAfterBulk(
  rq: CatalogDraftBulkActionRq,
  drafts: readonly CatalogDraft[],
  skipped: readonly CatalogSkippedDraft[]
): DraftStatusChange[] {
  const skippedIds = new Set(skipped.map((s) => s.draftId));
  const changes: DraftStatusChange[] = [];
  draftsTargetedBy(rq, drafts).forEach((draft) => {
    if (skippedIds.has(draft.draftId)) return;
    const to = targetStatusFor(rq.action, draft);
    if (to && to !== draft.status) changes.push({ draftId: draft.draftId, from: draft.status, to });
  });
  return changes;
}

/**
 * The request that undoes a bulk action (10-second undo toast): approve is
 * undone by unapprove, reject by restore, and the other way round. Targets the
 * ids that actually changed.
 */
export function undoRequestFor(
  rq: CatalogDraftBulkActionRq,
  changedDraftIds: readonly string[]
): CatalogDraftBulkActionRq | null {
  if (changedDraftIds.length === 0) return null;
  const inverse: Record<CatalogDraftAction, CatalogDraftAction> = {
    APPROVE: "UNAPPROVE",
    UNAPPROVE: "APPROVE",
    REJECT: "RESTORE",
    RESTORE: "REJECT",
  };
  return { action: inverse[rq.action], selector: "IDS", draftIds: [...changedDraftIds] };
}
